import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConfigBase } from './modules/common-shared-module/core/model/config/config.model';
import { UserEntityProfileType } from './modules/common-shared-module/core/model/user/user-entity-profile-type.model';
import { UserEntity } from './modules/common-shared-module/core/model/user/user-entity.model';
import { ConfigService } from './modules/common-shared-module/core/services/config/config.service';
import { SpinnerService } from './modules/common-shared-module/core/services/spinner/spinner.service';
import { MainMenuService } from './modules/common-shared-module/services/menu/main-menu.service';
import { DOCUMENT, Location } from '@angular/common';
import { GoogleTagManagerService } from './modules/common-shared-module/services/gtm/google-tag-manager.service';
import { ApplicationBase } from './modules/common-shared-module/core/model/application/application-link.model';
import { AppRecentService } from './modules/common-shared-module/core/services/application/app-recent.service';
import { HomeLinkService } from './modules/common-shared-module/core/services/application/home-link.service';
import { MatDialog } from '@angular/material/dialog';
import { EntityBase } from './modules/common-shared-module/core/model/entity/entity.model';
import { SessionService } from './modules/common-shared-module/core/services/session/session.service';
import { response } from './modules/common-shared-module/services/error/error-functions.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApplicationLinkType } from './modules/common-shared-module/core/model/application/application-link-type.model';
import { ApplicationOpenType } from './modules/common-shared-module/core/model/application/application-open-type.model';

class SettingsButton {
    public onlyOneOptionSettings: (() => void) | null = null

    public currentOption: (() => void) | null = null

    reset() {
        this.currentOption = this.onlyOneOptionSettings
    }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

    @ViewChild('searchEntities') searchEntitiesContent!: TemplateRef<unknown>

    @ViewChild('entityInfo') entityInfoContent!: TemplateRef<unknown>

    anonymous: boolean = false
    contrastMode: boolean = false
    menuItems: { name: string, url: string }[] = []
    // True when the current route is the 404 page: the shell doesn't paint its toolbar (the page has its own header)
    notFound: boolean = false
    loading: boolean = false
    spinnerSubscription: Subscription
    apps: ApplicationBase[] = []
    filteredEntities: { userEntity: UserEntity, entity: EntityBase }[] = []

    settingButton: SettingsButton = new SettingsButton()


    public showPreprodIndicator: boolean = false

    constructor(protected appRecentService: AppRecentService, private spinnerService: SpinnerService, private mainMenuService: MainMenuService, private router: Router, 
		public activatedRoute: ActivatedRoute, public configService: ConfigService, private location: Location, protected googleTagManagerService: GoogleTagManagerService, 
		protected dialog: MatDialog, protected sessionService: SessionService, protected snackBar: MatSnackBar, protected homeLinkService: HomeLinkService, @Inject(DOCUMENT) private document: Document) {
		
        this.spinnerSubscription = this.spinnerService.subscribe((loading) => {
            this.loading = loading
        })

        this.showPreprodIndicator = !configService.config.isProduction()
    }

    ngOnInit(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((item) => {
                this.menuItems = this.createBreadcrumbs(this.activatedRoute.root)
                this.notFound = this.isNotFoundRoute(this.activatedRoute.root)
                this.googleTagManagerService.pushOnDataLayer({ event: 'page', pageUrl: (<NavigationEnd>item).url })
                if ((<NavigationEnd>item).url == "/") {
                    this.settingButton.reset()
                } else if ((<NavigationEnd>item).url.startsWith("/cross")) {
                    this.settingButton.currentOption = this.goToCrossAdmin
                }
                else if ((<NavigationEnd>item).url.startsWith("/configens")) {
                    this.settingButton.currentOption = this.goToEnsAdmin
                } else if ((<NavigationEnd>item).url.startsWith("/eacadmin")) {
                    this.settingButton.currentOption = this.goToEacAdmin
                }
            })

        let currentPath = this.location.path()
        if (currentPath.indexOf("/fullscreen/form-print/") >= 0 || currentPath.indexOf("/print") == 0) {
            //This path doesn't need the config so return
            this.anonymous = true
            return
                }

        this.configService.init().subscribe(r => {
            this.filteredEntities = this.configService.config.entities

            this.appRecentService.syncAppsCodes(this.configService.config.connectedEntityDetail.userEntity.id).subscribe()

            this.homeLinkService.getHomeLinks().subscribe({
                next: apps => this.apps = apps,
                error: err => {
                    console.error('Error carregant els enllaços del home', err)
                    this.apps = []
                }
            })

            if (this.hasAdminProfile() == false) {
                if (this.configService.config.authorities.length == 1) {
                    if (this.configService.config.authorities[0].authority == UserEntityProfileType.ENS_MANAGER) {
                        this.settingButton.currentOption = this.goToCrossAdmin
                    } else if (this.configService.config.authorities[0].authority == UserEntityProfileType.SERVICE_MANAGER) {
                        this.settingButton.currentOption = this.goToEnsAdmin
                    }
                    this.settingButton.reset()
            }
            }
        })
    }

    get aocApps() {
    	return ApplicationBase.filterAndSortByVisualOrder(this.apps, ApplicationLinkType.AOC)
    }

    get externApps() {
    	return ApplicationBase.filterAndSortByVisualOrder(this.apps, ApplicationLinkType.EXTERNAL)
    }

    reloadApps() {
        this.homeLinkService.getHomeLinks().subscribe({
            next: apps => this.apps = apps,
            error: err => console.error('Error carregant els enllaços del home', err)
        })
    }

    clickOnHome() {
        this.settingButton.reset()
    }

    hasAnyProfile(): boolean {
        return this.hasEnsAdminOrEnsServiceProfile() || this.hasAdminProfile() || this.hasManagerApps();
    }

    hasAdminProfile(): boolean {
        if (this.configService.config == null) {
            return false
        }
        return this.configService.config.hasAdminProfile()
    }

    hasEnsAdminOrEnsServiceProfile(): boolean {
        if (this.configService.config == null) {
            return false
        }

        return this.configService.config.authorities.filter(p => p.authority == UserEntityProfileType.ENS_MANAGER || p.authority == UserEntityProfileType.SERVICE_MANAGER).length > 0
    }

    hasEnsAdmin(): boolean {
        if (this.configService.config == null) {
            return false
        }

        return this.configService.config.authorities.filter(p => p.authority == UserEntityProfileType.ENS_MANAGER).length > 0 
    }

    hasManagerApps(): boolean {
        if (this.configService.config == null) {
            return false
        }

        return this.configService.config.hasManagerApps();
    }

    onMenu() {
        this.mainMenuService.click()
    }

    ngOnDestroy(): void {
        this.spinnerSubscription.unsubscribe()
    }

    private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: { name: string, url: string }[] = []): { name: string, url: string }[] {
        const children: ActivatedRoute[] = route.children

        if (children.length === 0) {
            return breadcrumbs
        }

        for (const child of children) {
            const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/')
            if (routeURL !== '') {
                url += `/${routeURL}`
            }

            const label = child.snapshot.data["breadcrumb"]
            if (label != null) {
                //push only if not present
                if (breadcrumbs.filter(p => p.name == label).length == 0) {
                    breadcrumbs.push({ name: label, url: url })
                }
            }

            return this.createBreadcrumbs(child, url, breadcrumbs)
        }

        return []
    }

    private isNotFoundRoute(route: ActivatedRoute): boolean {
        let current: ActivatedRoute = route
        while (current.firstChild) {
            current = current.firstChild
        }
        return current.snapshot.data['notFound'] === true
    }

    isAdminOption(): boolean {
        if (this.menuItems.length > 0 && (this.menuItems[0].url == '/configens' || this.menuItems[0].url == '/eacadmin' || this.menuItems[0].url == '/cross')) {
            return true
        }
        return false
    }

    goToEnsAdmin = () => {
        this.settingButton.currentOption = this.goToEnsAdmin

        this.router.navigate(["configens"])
    }

    goToCrossAdmin = () => {
        this.settingButton.currentOption = this.goToCrossAdmin

        this.router.navigate(["cross"])
    }

    goToEacAdmin = () => {
        this.settingButton.currentOption = this.goToEacAdmin

        this.router.navigate(["eacadmin"])
    }

    changeEntity(userEntityId: number, entityId: number): void {
        this.configService.config.putEntityAsRecent(entityId)
        this.configService.config.reOrderEntities()

        this.configService.changeEns(userEntityId).subscribe(r => {
            window.location.replace("/home/catproc/public-home")
        })
    }

    getConfig(): ConfigBase {
        return this.configService.config
    }

    openApp(app: ApplicationBase) {
        this.appRecentService.putApp(app.code, this.configService.config.connectedEntityDetail.userEntity.id).subscribe(() => {
            let appUrl = app.url

            if (appUrl.includes("@@HOSTNAME_MWF@@")) {
                const win = this.document.defaultView!
                const url = new URL(win.location.toString())

                let theUrl = url.origin + '/mwfst'
                if (url.origin.startsWith('http://')) {
                    theUrl = this.configService.config.mwfAddress
                }
                appUrl = appUrl.replace("@@HOSTNAME_MWF@@", theUrl)
            }

            const finalUrl = appUrl.replace(
                "@@ENTITY_AUTH@@",
                this.configService.config.connectedEntityDetail.entity.entityInfo.ine10
            )

            if (app.openMode === ApplicationOpenType.NEW_TAB) {
                window.open(finalUrl, "_blank")
            } else {
                window.location.assign(finalUrl)
            }
        })

        
    }

    openEntities() {
        this.filteredEntities = this.configService.config.entities
        this.dialog.open(this.searchEntitiesContent, {
            hasBackdrop: true,
            height: "730px",
            panelClass: "no-dialog-borders"
        })
    }

    openEntityInfo() {
        this.dialog.open(this.entityInfoContent, {
            hasBackdrop: true,
            height: "409px",
            width: "695px",
            panelClass: "no-dialog-borders"
        })
    }

    doFilterEntities(filter: string) {
        this.filteredEntities = this.configService.config.entities.filter(p => p.entity.entityInfo.name.toLocaleLowerCase().indexOf(filter.toLocaleLowerCase()) >= 0)
    }

    doLogout() {
        this.sessionService.logout().subscribe(r => {
            response.onResponse(r, this.snackBar)
        })
    }

    now(): Date {
        return new Date()
    }

    goToSuport() {
        window.open("https://suport.aoc.cat/ca-ES/category/?id=CAT-01320", "_blank")
    }

    goToSchool() {
        window.open("https://campus.aoc.cat/", "_blank")
    }

    goToBlog() {
        window.open("https://www.aoc.cat/blog/category/tramitacio-administracions/", "_blank")
    }

    goToGlossari() {
        window.open("https://suport-eacat.aoc.cat/hc/ca/articles/20217158937501-Glossari-de-termes", "_blank")
    }

}
